import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Radio, X, Repeat, Clock, CheckCircle2 } from "lucide-react";

const fmt = (iso) => (iso ? new Date(iso).toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" }) : "—");
const STATUS = { scheduled: "text-gold border-gold/40", sending: "text-amber-300 border-amber-500/40", sent: "text-emerald-300 border-emerald-500/40", cancelled: "text-text-muted border-white/15" };

export function ScheduledBroadcasts({ refreshKey }) {
  const [rows, setRows] = useState(null);
  const load = () => api.get("/admin/broadcast/scheduled").then(({ data }) => setRows(data)).catch(() => setRows([]));
  useEffect(() => { load(); }, [refreshKey]);
  const cancel = async (b) => {
    try {
      const { data } = await api.delete(`/admin/broadcast/scheduled/${b.id}`);
      setRows((r) => r.map((x) => (x.id === b.id ? data : x)));
      toast.success("Broadcast cancelled");
    } catch (e) { toast.error(e.response?.data?.detail || "Could not cancel"); }
  };
  if (rows === null) return <p className="text-text-muted">Loading…</p>;
  const pending = rows.filter((r) => r.status === "scheduled" || r.status === "sending");
  const past = rows.filter((r) => r.status === "sent" || r.status === "cancelled");
  const Row = ({ b }) => (
    <div data-testid={`sched-row-${b.id}`} className="grid grid-cols-1 md:grid-cols-[1fr_190px_180px_auto] gap-3 md:gap-4 px-5 py-4 items-center">
      <div className="min-w-0">
        <p className="text-sm line-clamp-2">{b.body}</p>
        <p className="text-[10px] uppercase tracking-[0.12em] text-text-muted mt-1">to {b.audience} · by {b.created_by_name}{b.runs ? ` · sent ${b.runs}×` : ""}{b.last_count != null ? ` · last reach ${b.last_count}` : ""}</p>
      </div>
      <p className="text-xs text-text-muted flex items-center gap-1.5"><Clock size={12} /> {fmt(b.send_at)}</p>
      <div className="flex items-center gap-2">
        <span className={`text-[10px] uppercase tracking-[0.12em] border px-2.5 py-1 rounded-full ${STATUS[b.status]}`}>{b.status}</span>
        {b.repeat !== "none" && <span title={`Repeats ${b.repeat}`} className="text-[10px] uppercase tracking-[0.12em] text-text-muted flex items-center gap-1"><Repeat size={11} /> {b.repeat}</span>}
      </div>
      {b.status === "scheduled" ? (
        <button data-testid={`sched-cancel-${b.id}`} onClick={() => cancel(b)}
          className="flex items-center gap-1 px-3 py-2 rounded-full border border-white/15 text-text-muted text-[10px] uppercase tracking-[0.12em] hover:text-amber-300 hover:border-amber-500/40 transition-colors"><X size={12} /> Cancel</button>
      ) : <span className="text-text-muted"><CheckCircle2 size={14} className="opacity-40" /></span>}
    </div>
  );
  return (
    <div data-testid="broadcasts-panel" className="space-y-8">
      <div className="rounded-2xl border border-white/10 bg-surface overflow-hidden">
        <div className="px-5 py-3 text-[10px] uppercase tracking-[0.15em] text-text-muted border-b border-white/8 flex items-center gap-2"><Radio size={12} className="text-gold" /> Pending ({pending.length})</div>
        {pending.length === 0 ? <p data-testid="sched-empty" className="px-5 py-8 text-sm text-text-muted">Nothing scheduled. Use Broadcast → Schedule to queue an announcement.</p>
          : <div className="divide-y divide-white/5">{pending.map((b) => <Row key={b.id} b={b} />)}</div>}
      </div>
      {past.length > 0 && (
        <div className="rounded-2xl border border-white/10 bg-surface/60 overflow-hidden">
          <div className="px-5 py-3 text-[10px] uppercase tracking-[0.15em] text-text-muted border-b border-white/8">History</div>
          <div className="divide-y divide-white/5">{past.slice(0, 30).map((b) => <Row key={b.id} b={b} />)}</div>
        </div>
      )}
    </div>
  );
}
